import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { httpsCallable } from 'firebase/functions';
import { FUNCTIONS } from 'src/contexts/FirebaseContext';
import { PassCultureData, PassCultureEventStatus } from 'src/@types/passculture';
import { Timestamp } from 'firebase/firestore';

interface UpdateAllPassCultureStocksRequest {
  eventId: string;
  beginningDatetime: string;
  bookingLimitDatetime?: string;
}

interface UpdateAllPassCultureStocksResponse {
  success: boolean;
  message?: string;
  totalCount?: number;
  successCount?: number;
  failCount?: number;
  results?: Array<{
    stockId: string;
    success: boolean;
    error?: string;
  }>;
  error?: string;
}

// Define interfaces for the API
interface PublishEventRequest {
  eventId: string;
}

interface PublishEventResponse {
  success: boolean;
  passCultureId?: string;
  data?: any;
  error?: string;
}

interface CheckStatusRequest {
  eventId: string;
}

interface CheckStatusResponse {
  success: boolean;
  status?: PassCultureEventStatus;
  data?: any;
  error?: string;
}

export interface AddEventStocksParams {
    eventId: string;
    useExistingTickets?: boolean;
    dates?: Array<{
      beginningDatetime: string;
      bookingLimitDatetime: string;
      priceCategoryId: string;
      quantity: number;
      idAtProvider: string;
    }>;
  }
  
  export interface AddEventStocksResponse {
    success: boolean;
    message?: string;
    error?: string;
    data?: any;
  }
  

// Create the Pass Culture API
export const PassCultureApi = createApi({
  reducerPath: 'passCultureApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['PassCultureEvent'],
  endpoints: (builder) => ({
    // Publish an event to Pass Culture
    publishEvent: builder.mutation<PublishEventResponse, PublishEventRequest>({
      async queryFn(request) {
        try {
          const publishFn = httpsCallable(FUNCTIONS, 'passculture-publishEventToPassCultureCallable');
          const result = await publishFn(request);
          console.log('Publish result:', result.data);
          
          return { 
            data: result.data as PublishEventResponse 
          };
        } catch (error: any) {
          console.error('Error publishing event to Pass Culture:', error);
          return { 
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while publishing the event'
            }
          };
        }
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'PassCultureEvent', id: eventId }
      ],
    }),
    addEventStocks: builder.mutation<AddEventStocksResponse, AddEventStocksParams>({
        queryFn: async (params) => {
          try {
            const addEventStocksCallable = httpsCallable(
                FUNCTIONS,
              'passculture-addEventStocksToPassCultureCallable'
            );
            
            const result = await addEventStocksCallable(params);
            return { data: result.data as AddEventStocksResponse };
          } catch (error: any) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: error.message || 'Failed to add stocks to Pass Culture event',
              },
            };
          }
        },
        invalidatesTags: ['PassCultureEvent'],
      }),
    
    // Check status of a Pass Culture event
    checkEventStatus: builder.mutation<CheckStatusResponse, CheckStatusRequest>({
      async queryFn(request) {
        try {
          const checkStatusFn = httpsCallable(FUNCTIONS, 'passculture-getPassCultureEventStatusCallable');
          const result = await checkStatusFn(request);
          
          return { 
            data: result.data as CheckStatusResponse 
          };
        } catch (error: any) {
          console.error('Error checking Pass Culture event status:', error);
          return { 
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while checking event status'
            }
          };
        }
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'PassCultureEvent', id: eventId }
      ],
    }),
    updateAllPassCultureStocks: builder.mutation
    <UpdateAllPassCultureStocksResponse, 
    {eventId: string, startDate: Date | Timestamp, stopSellDate?: Date | Timestamp}
  >({
    async queryFn({ eventId, startDate, stopSellDate }) {
      try {
        // Convert Date or Timestamp to ISO string for the API
        const beginningDatetime = startDate instanceof Timestamp 
          ? new Date(startDate.seconds * 1000).toISOString()
          : startDate.toISOString();
        
        // Convert stopSellDate if provided
        const bookingLimitDatetime = stopSellDate 
          ? (stopSellDate instanceof Timestamp 
              ? new Date(stopSellDate.seconds * 1000).toISOString()
              : stopSellDate.toISOString())
          : undefined;
        
        const updateStocksFn = httpsCallable(
          FUNCTIONS, 
          'passculture-updateAllPassCultureStocksCallable'
        );
        
        const result = await updateStocksFn({
          eventId,
          beginningDatetime,
          ...(bookingLimitDatetime && { bookingLimitDatetime })
        });
        console.log('Update Pass Culture stocks result:', result.data);
        
        return { 
          data: result.data as UpdateAllPassCultureStocksResponse 
        };
      } catch (error: any) {
        console.error('Error updating PassCulture stocks:', error);
        return { 
          error: {
            status: error?.code || 'UNKNOWN_ERROR',
            data: error?.message || 'An error occurred while updating PassCulture stocks'
          }
        };
      }
    },
    invalidatesTags: (result, error, { eventId }) => [
      { type: 'PassCultureEvent', id: eventId }
    ],
  }),
  }),
});

// Export hooks for use in functional components
export const {
  usePublishEventMutation,
  useCheckEventStatusMutation,
  useAddEventStocksMutation,
  useUpdateAllPassCultureStocksMutation,
} = PassCultureApi;